import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import BaseErrors from "@components/fields/BaseErrors";
import FormButtons from "@components/fields/FormButtons";
import Input from "@components/fields/Input";
import RichTextarea from "@components/fields/RichTextarea";
import Select, { Option } from "@components/fields/Select";
import Form from "@components/forms/Form";
import {
  MessageTemplateCreateDocument,
  MessageTemplateCreateMutation,
  MessageTemplateCreateMutationVariables,
  MessageTemplateFragment,
  MessageTemplateUpdateDocument,
  MessageTemplateUpdateMutation,
  MessageTemplateUpdateMutationVariables
} from "@typing/Generated";
import { handleMutationResponse, standardRichTextareaMenuControls } from "@utils/formUtils";

type FormData = {
  categoryId: string | null;
  categoryTitle: string | null;
  content: string;
  title: string;
};

type Props = {
  autofocus: boolean;
  categoryOptions: Option[];
  messageTemplate?: MessageTemplateFragment | null;
  onCancel: () => void;
  onError: () => void;
  onSuccess: () => void;
};

const MessageTemplateForm = ({ autofocus, categoryOptions, messageTemplate, onCancel, onError, onSuccess }: Props) => {
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const defaultValues: FormData = {
    categoryId: messageTemplate?.messageTemplateCategory.id ?? null,
    categoryTitle: messageTemplate?.messageTemplateCategory.title ?? null,
    content: messageTemplate?.content ?? "",
    title: messageTemplate?.title ?? ""
  };

  const [createMessageTemplate, { client }] = useMutation<
    MessageTemplateCreateMutation,
    MessageTemplateCreateMutationVariables
  >(MessageTemplateCreateDocument, {
    update: () => {
      client.resetStore();
    }
  });

  const [updateMessageTemplate] = useMutation<MessageTemplateUpdateMutation, MessageTemplateUpdateMutationVariables>(
    MessageTemplateUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      categoryId: yup.string().required(intl.formatMessage({ id: "errors.messageTemplate.categoryId.blank" })),
      categoryTitle: yup.string().nullable().defined(),
      content: yup.string().required(intl.formatMessage({ id: "errors.messageTemplate.content.blank" })),
      title: yup.string().required(intl.formatMessage({ id: "errors.messageTemplate.title.blank" }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    if (messageTemplate?.id) {
      return updateMessageTemplate({
        variables: {
          ...form.getValues(),
          id: messageTemplate.id,
          messageTemplateCategoryId: form.getValues().categoryId ?? ""
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.messageTemplateUpdate,
          intl,
          onError,
          onSuccess: () => {
            if (onSuccess) {
              onSuccess();
            }
          },
          setBaseErrors,
          setError: form.setError,
          txBase: "errors.messageTemplate"
        });
      });
    }
    return createMessageTemplate({
      variables: {
        ...form.getValues(),
        messageTemplateCategoryId: form.getValues().categoryId ?? ""
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.messageTemplateCreate,
        intl,
        onSuccess,
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.messageTemplate"
      });
    });
  }, [messageTemplate?.id, createMessageTemplate, form, updateMessageTemplate, intl, onError, onSuccess]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <Input<FormData>
        autofocus={autofocus}
        error={form.formState.errors.title}
        label={intl.formatMessage({ id: "forms.messageTemplate.titleLabel" })}
        register={form.register("title")}
        required
        spellcheck
      />
      <RichTextarea<FormData>
        controls={[...standardRichTextareaMenuControls, "inAppLinks", "mergeTags"]}
        error={form.formState.errors.content}
        form={form}
        label={intl.formatMessage({ id: "forms.messageTemplate.contentLabel" })}
        name="content"
        required
        showArticleLinksEvenWithoutJourneyId
      />
      <Select<FormData>
        control={form.control}
        error={form.formState.errors.categoryId}
        label={intl.formatMessage({ id: "forms.messageTemplate.categoryLabel" })}
        name="categoryId"
        options={categoryOptions}
        required
      />
      <FormButtons
        hookForm={form}
        onCancel={onCancel}
        saveKey={messageTemplate?.id ? "dictionary.saveChanges" : "pages.expert.messageTemplates.create"}
      />
    </Form>
  );
};

export default MessageTemplateForm;
